import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { SignOut, UsersThree, Sun, Moon } from "@phosphor-icons/react";
import { NRSLogo, NRSWordmark } from "./NRSLogo";
import { useT, LangToggle } from "../lib/i18n";
import AssistantLauncher from "./AssistantLauncher";
import { api } from "../lib/api";
import { hasAiAccess } from "../lib/planEntitlements";

export default function AppShell({ children, assistantProject = null, assistantVersion = null }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const t = useT();
  const [memberships, setMemberships] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem("rg_theme") || "dark");
  const currentWs = localStorage.getItem("rg_workspace") || "";
  const currentRole = memberships.find((m) => m.owner_id === currentWs)?.role || "";
  const selectedSection = location.pathname === "/company" ? "company" : "";
  const mainNavigation = [
    { key: "company", label: t("nav.main.company"), to: "/company" },
  ];

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("rg_theme", theme);
  }, [theme]);

  useEffect(() => {
    if (!user) return;
    api.get("/team/memberships").then((r) => setMemberships(r.data || [])).catch(() => {});
  }, [user]);

  function switchWorkspace(ws) {
    if (ws) localStorage.setItem("rg_workspace", ws);
    else localStorage.removeItem("rg_workspace");
    window.location.href = "/dashboard";
  }

  return (
    <div className="min-h-screen bg-[#070B14] text-[#F8FAFC]">
      <header className="app-header sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-y-2">
          <div className="order-1 flex min-w-0 items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="header-logo">
              <NRSLogo size={28} />
              <div className="flex flex-col leading-tight">
                <NRSWordmark />
                <span className="max-w-[190px] text-[8px] leading-tight text-slate-400 sm:max-w-none">{t("header.tagline")}</span>
              </div>
            </Link>
            <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
              className="p-1.5 rounded-sm border border-[#1E2C42] bg-[#0E1626] text-slate-400 hover:text-cyan-400 hover:bg-[#18243B] transition-colors">
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            </div>
          <nav
            aria-label={t("nav.main.label")}
            data-testid="primary-navigation"
            className="order-2 -mx-4 flex w-[calc(100%+2rem)] items-center gap-1 overflow-x-auto border-y border-[#1E2C42] px-4 py-1 sm:mx-0 sm:w-auto sm:flex-1 sm:justify-center sm:overflow-visible sm:border-0 sm:px-3 sm:py-0"
          >
            {mainNavigation.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                aria-current={selectedSection === item.key ? "page" : undefined}
                data-testid={`main-nav-${item.key}`}
                className={`shrink-0 whitespace-nowrap rounded-sm px-3 py-2 text-[11px] font-semibold transition-colors ${
                  selectedSection === item.key
                    ? "bg-cyan-600 text-white"
                    : "text-slate-300 hover:bg-[#18243B] hover:text-cyan-300"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="order-3 flex w-full flex-wrap items-center justify-end gap-2 text-xs font-mono sm:w-auto sm:gap-3">
            <LangToggle />
            {(memberships.length > 0 || currentWs) && (
              <span className="inline-flex items-center gap-1">
                <UsersThree size={14} className="text-cyan-400" />
                <select value={currentWs} onChange={(e) => switchWorkspace(e.target.value)} data-testid="workspace-select"
                  className="border border-[#1E2C42] px-2 py-1.5 rounded-sm bg-[#0E1626] text-xs focus:border-cyan-500 focus:outline-none max-w-[42vw] sm:max-w-[180px]">
                  <option value="">{t("team.my_workspace")}</option>
                  {memberships.map((m) => (
                    <option key={m.owner_id} value={m.owner_id}>
                      {(m.owner_company || m.owner_email).slice(0, 26)} · {t(`team.role.${m.role}`)}
                    </option>
                  ))}
                </select>
              </span>
            )}
            {currentWs && currentRole === "viewer" && (
              <span className="chip" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="readonly-badge">{t("team.readonly")}</span>
            )}
            <span
              className="hidden md:inline"
              style={{ color: theme === "light" ? "#334155" : "#CBD5E1" }}
              data-testid="header-user-email"
            >
              {user?.email}
            </span>
            <span className="chip chip-IIa hidden sm:inline-flex" data-testid="header-role">{(user?.role || "").replace("_", " ")}</span>
            <button data-testid="header-logout" onClick={async () => { await logout(); nav("/login"); }}
              aria-label={t("nav.logout")} title={t("nav.logout")}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 border border-[#1E2C42] hover:bg-cyan-950/40 hover:border-cyan-500/50 hover:text-cyan-300 rounded-sm transition-colors">
              <SignOut size={14} /> <span className="hidden sm:inline">{t("nav.logout")}</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>
      <footer className="border-t border-[#1E2C42] mt-12 bg-[#0E1626]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 text-xs font-mono text-slate-400 flex flex-col gap-1 sm:flex-row sm:justify-between">
          <span>{t("footer.left")}</span>
          <span>{t("footer.right")}</span>
        </div>
      </footer>

      {hasAiAccess(user) && <AssistantLauncher project={assistantProject} version={assistantVersion} />}
    </div>
  );
}
